import {
  FocusTrap,
  FocusTrapFactory,
  InteractivityChecker,
} from '@angular/cdk/a11y';
import { Platform } from '@angular/cdk/platform';
import {
  BasePortalOutlet,
  CdkPortalOutlet,
  ComponentPortal,
  TemplatePortal,
} from '@angular/cdk/portal';
import {
  Component,
  ComponentRef,
  ElementRef,
  EmbeddedViewRef,
  Injector,
  NgZone,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
  afterNextRender,
  inject,
} from '@angular/core';
import { SideDialogOptions } from './side-dialog-options.class';
import { SideDialogRef } from './side-dialog-ref.class';

@Component({
  selector: 'side-dialog-container',
  template: `<ng-template cdkPortalOutlet />`,
  imports: [CdkPortalOutlet],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  host: {
    class: 'side-dialog-container',
    tabindex: '-1',
  },
})
export class SideDialogContainer extends BasePortalOutlet {
  @ViewChild(CdkPortalOutlet, { static: true }) _portalOutlet: CdkPortalOutlet;

  private _injector = inject(Injector);
  private _element = inject(ElementRef);
  private _platform = inject(Platform);
  private _focusTrap: FocusTrap | null = null;
  private _focusTrapFactory = inject(FocusTrapFactory);
  private _interactivityChecker = inject(InteractivityChecker);
  private _ngZone = inject(NgZone);
  private _renderer = inject(Renderer2);
  private _options: SideDialogOptions = inject(SideDialogOptions);
  private _sideDialogRef: SideDialogRef = inject(SideDialogRef);
  private _elementRef: ElementRef = inject(ElementRef);

  attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
    if (this._portalOutlet.hasAttached()) {
      throw new Error('Portal already attached');
    }

    const result = this._portalOutlet.attachComponentPortal(portal);
    this.contentAttached();
    this._elementRef.nativeElement.classList.add(
      'side-dialog-container__component'
    );
    return result;
  }

  attachTemplatePortal<C>(portal: TemplatePortal<C>): EmbeddedViewRef<C> {
    if (this._portalOutlet.hasAttached()) {
      throw new Error('Portal already attached');
    }

    const result = this._portalOutlet.attachTemplatePortal(portal);
    this.contentAttached();
    this._elementRef.nativeElement.classList.add(
      'side-dialog-container__template'
    );
    return result;
  }

  private markeRendered(): void {
    const panel = this._element.nativeElement.closest(
      '.side-dialog-panel'
    ) as HTMLElement;

    if (panel) {
      setTimeout(() => {
        panel.classList.add('side-dialog-panel__rendered');

        setTimeout(() => {
          this._sideDialogRef.opened.emit();
        }, 400);
      });
    }
  }

  private contentAttached(): void {
    this.initializeFocusTrap();
    this.trapFocus();
  }

  private trapFocus(): void {
    afterNextRender(
      () => {
        switch (this._options.autoFocus) {
          case false:
          case 'dialog':
            if (!this.containsFocus()) {
              this._element.nativeElement.focus();
            }
            break;
          case true:
          case 'first-tabbable':
            const success = this._focusTrap?.focusInitialElement();

            if (!success) {
              if (this._element.nativeElement.focus) {
                this._element.nativeElement.focus();
              }
            }
            break;
          case 'first-heading':
            this.cssFocus('h1, h2, h3, h4, h5, h6, [role="heading"]');
            break;
          default:
            this.cssFocus(this._options.autoFocus! as string);
            break;
        }

        this.markeRendered();
      },
      { injector: this._injector }
    );
  }

  private cssFocus(selector: string): void {
    const element = this._element.nativeElement.querySelector(
      selector
    ) as HTMLElement | null;

    if (element) {
      this.forceFocus(element);
    }
  }

  private forceFocus(element: HTMLElement): void {
    if (!this._interactivityChecker.isFocusable(element)) {
      element.tabIndex = -1;

      this._ngZone.runOutsideAngular(() => {
        const cb = () => {
          deregisterBlur();
          deregisterMousedown();
          element.removeAttribute('tabindex');
        };

        const deregisterBlur = this._renderer.listen(element, 'blur', cb);
        const deregisterMousedown = this._renderer.listen(
          element,
          'mousedown',
          cb
        );
      });
    }

    element.focus();
  }

  private containsFocus(): boolean {
    return (
      this._element.nativeElement === document.activeElement ||
      this._element.nativeElement.contains(document.activeElement)
    );
  }

  private initializeFocusTrap(): void {
    if (this._platform.isBrowser) {
      this._focusTrap = this._focusTrapFactory.create(
        this._element.nativeElement
      );
    }
  }
}
